import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { InstallmentDataQuery } from '@packages/gql/generated/shopping/graphql';

import { Notification } from '@packages/shared/src/ui/Notification/Notification';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import type { InstallmentsCalulatorCalculationValuta } from '../../types';
import { getNormalizedValue } from './getNormalizedValue/getNormalizedValue';
import { calulatorMessages } from '../CalculatorLayouts/messages';

type InstallmentData = NonNullable<InstallmentDataQuery['installment']>;

export type EditableBasePriceProps = Pick<InstallmentData, 'currency' | 'minValue' | 'maxValue'> & {
  /**
   * the initial base price in Euro as float (e.g. 13.37)
   */
  calculationValuta: InstallmentsCalulatorCalculationValuta;
  /**
   * handler function that will be executed after input
   *
   * @price - input price in Euro as float (e.g. 13.37)
   */
  onChangeHandler: (price: number) => void;
};

type Mode = 'EDIT' | 'DISPLAY';

export const EditableBasePrice = ({
  calculationValuta,
  currency,
  onChangeHandler,
  minValue,
  maxValue,
}: EditableBasePriceProps) => {
  const { formatMessage } = useIntl();
  const { formatPrice } = useFormatPrice();
  const [mode, setMode] = useState<Mode>('DISPLAY');
  const [error, setError] = useState<null | string>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current && mode === 'EDIT') {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [mode, inputRef]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (error !== null) {
      timer = setTimeout(() => {
        setError(null);
        clearTimeout(timer);
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [error, setError]);

  const setEditMode = () => {
    setMode('EDIT');
  };

  const handleInput = () => {
    if (inputRef.current) {
      let value = getNormalizedValue(inputRef.current.value);

      // check if input is smaller then minValue given from the calculation result
      if (value < minValue) {
        value = minValue;
        setError(
          formatMessage(
            {
              defaultMessage:
                'Der Preis wurde automatisch für dich angepasst. Der Minimalbetrag muss {minValue} betragen.',
              id: 'ic-editableBasePrice-notification-min',
            },
            {
              minValue: formatPrice(minValue, currency, true),
            },
          ),
        );
      }
      // check if input is smaller then maxValue given from the calculation result
      else if (maxValue > 0 && value > maxValue) {
        value = maxValue;
        setError(
          formatMessage(
            {
              defaultMessage:
                'Der gültige Wertebereich für eine Ratenberechnung liegt zwischen {minValue} und {maxValue}. Der Preis wurde automatisch für dich angepasst.',
              id: 'ic-editableBasePrice-notification-minMax',
            },
            {
              minValue: formatPrice(minValue, currency, true),
              maxValue: formatPrice(maxValue, currency, true),
            },
          ),
        );
      }

      inputRef.current.value = formatPrice(value, currency, true);

      setMode('DISPLAY');
      onChangeHandler(value);
    }
  };

  return (
    <div>
      {error !== null && (
        <Notification severity="error" onClose={() => setError(null)}>
          {error}
        </Notification>
      )}
      <div
        className={`relative mx-auto max-w-[11rem] px-[20px] py-1 ${mode === 'DISPLAY' ? 'bg-transparent' : 'bg-color-background-base'}`}
      >
        <input
          defaultValue={formatPrice(calculationValuta, currency, true)}
          className="text-size-h4 block w-full border-0 bg-transparent text-center outline-0"
          disabled={mode === 'DISPLAY'}
          ref={inputRef}
          onBlur={handleInput}
          onKeyUp={(event) => event.key === 'Enter' && event.currentTarget.blur()}
        />
        <div className="absolute top-1/2 right-0 size-[20px] -translate-y-1/2 cursor-pointer">
          {mode === 'DISPLAY' ? (
            <button
              type="button"
              className="size-full"
              onClick={setEditMode}
              aria-label={`${formatMessage(calulatorMessages.basePrice)} ${formatMessage({
                id: 'ic-editableBasePrice-button-edit-aria-label',
                defaultMessage: 'bearbeiten',
              })}`}
              // the key is needed that react can seperate the buttons
              // otherwise a click on the confirm button will result in a click of the edit button
              // because the button will be rerendered while the click process is going on and
              // react can't seperate between the click start and end of both buttons without a key
              key={mode}
            >
              <span className="icon-emp-edit size-full" />
            </button>
          ) : (
            <button
              type="button"
              className="size-full"
              onClick={handleInput}
              aria-label={`${formatMessage(calulatorMessages.basePrice)} ${formatMessage({
                id: 'ic-editableBasePrice-button-confirm-aria-label',
                defaultMessage: 'bestätigen',
              })}`}
              key={mode}
            >
              <span className="icon-emp-enter-confirm-submit size-full" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
